/* Atlas V3 Desktop — Signals (The Wire).
   Signal + news feed mirroring mobile Wire: loud-first TODAY/LATEST bucket
   (conviction → options magnitude → recency) + EARLIER THIS WEEK, ALL /
   SIGNALS / NEWS filters with signal-type chips, star-to-watchlist, a
   While-You-Slept overnight lead (indigo — a sanctioned violet exception),
   and a constellation strip. Rows open the NewsSheet reader or the dock. */
import { useMemo, useState } from 'react'
import { StockLogo } from '@/v2/components/StockLogo'
import { SignalBadge, SIGNAL_MAP, signalWriteup, signalToSheet, useTicker } from '@/v2/v3/lib'
import { useSignalsRecent, useNews, useConstellationAlerts } from '@/v2/hooks/useSupabaseData'
import { useWatchlists } from '@/v2/hooks/useWatchlists'
import { Crossfade, SkelDisp, SkelText, SkelCircle, enterStyle } from '../qol'

function fmtTime(ts) {
  if (!ts) return ''
  try { return new Date(ts).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'America/New_York' }) + ' ET' } catch { return '' }
}
function fmtRel(iso) {
  if (!iso) return ''
  const m = Math.floor((Date.now() - new Date(iso).getTime()) / 60000)
  if (m < 1) return 'just now'
  if (m < 60) return `${m}m ago`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h}h ago`
  return `${Math.floor(h / 24)}d ago`
}
function ageHours(iso) {
  if (!iso) return Infinity
  return (Date.now() - new Date(iso).getTime()) / 3600000
}
function fmtPrem(v) {
  if (!v) return null
  if (v >= 1e9) return `$${(v / 1e9).toFixed(1)}B`
  if (v >= 1e6) return `$${(v / 1e6).toFixed(1)}M`
  if (v >= 1e3) return `$${(v / 1e3).toFixed(0)}K`
  return `$${Math.round(v)}`
}

export function Signals({ openNews }) {
  const openTicker = useTicker()
  const { events, loading: sigLoading } = useSignalsRecent(200)
  const { news, loading: newsLoading } = useNews()
  const { alerts } = useConstellationAlerts()
  const { watchlists, addTicker } = useWatchlists()
  const [filter, setFilter] = useState('all') // all | signals | news
  const [typeF, setTypeF] = useState('all')
  const [starred, setStarred] = useState(() => new Set())
  const [toast, setToast] = useState(null)

  function onStar(ticker) {
    const tk = String(ticker).toUpperCase()
    setStarred((prev) => new Set(prev).add(tk))
    const listId = watchlists?.[0]?.id
    if (listId) addTicker(listId, tk)
    setToast(`${tk} added to watchlist`)
    setTimeout(() => setToast((t) => (t && t.startsWith(tk) ? null : t)), 1700)
  }

  const signalItems = useMemo(() => (events || []).map((e) => {
    const dir = String(e.direction || '').toLowerCase()
    const up = dir === 'up' || dir === 'bullish' || dir === 'positive'
    const down = dir === 'down' || dir === 'bearish' || dir === 'negative'
    // api/signal-events selects the column `detail` (singular) — tolerate both
    // spellings like mobile Wire.jsx does.
    const d = e.details || e.detail || {}
    // Options magnitude tiebreaker — max(cpr, 1/cpr) is large for lopsided
    // call- OR put-heavy flow, ~1 for balanced (mirrors mobile eventToItem).
    const cpr = Number(d.call_put_ratio)
    return {
      kind: 'signal', id: `s-${e.id}`, t: e.ticker, type: e.signal_type,
      sentiment: up ? 'bullish' : down ? 'bearish' : null,
      // confidence is a 0-100 column — normalize to 0-1 (mirrors mobile eventToItem)
      conv: typeof e.confidence === 'number' ? (e.confidence > 1 ? e.confidence / 100 : e.confidence) : null,
      mag: Number.isFinite(cpr) && cpr > 0 ? Math.max(cpr, 1 / cpr) : 0,
      prem: Number(d.total_premium) || 0, // real-money tiebreaker (options signals)
      headline: signalWriteup(e), why: d.summary || null,
      ts: e.signal_date, time: fmtRel(e.signal_date),
    }
  }), [events])

  const newsItems = useMemo(() => {
    const seen = new Set()
    return (news || []).filter((a) => { if (!a.url || seen.has(a.url)) return false; seen.add(a.url); return true })
      .map((a) => ({ kind: 'news', id: `n-${a.id}`, t: a.ticker, type: 'news', sentiment: null, headline: a.headline, why: a.source || 'News', ts: a.published_at, time: fmtRel(a.published_at), summary: a.summary, image: a.image_url, url: a.url }))
  }, [news])

  const sigTypes = useMemo(() => [...new Set(signalItems.map((s) => s.type))].sort(), [signalItems])
  // Magnitude scale for the premium bars — normalized to the largest options
  // premium currently loaded, so big flow visually dominates the feed.
  const maxPrem = useMemo(() => signalItems.reduce((m, s) => Math.max(m, s.prem || 0), 0), [signalItems])

  // Loud-first grouping, mirroring mobile Wire: the freshest 24h batch
  // (anchored to the NEWEST item, not the wall clock — signals land in one
  // nightly cron run) leads with high-conviction signals first (conv → options
  // magnitude → recency); everything else stays chronological underneath.
  const groups = useMemo(() => {
    let view = filter === 'signals' ? signalItems : filter === 'news' ? newsItems : [...signalItems, ...newsItems]
    if (filter === 'signals' && typeF !== 'all') view = view.filter((i) => i.type === typeF)
    const LOUD = 0.75
    const byAge = (a, b) => ageHours(a.ts) - ageHours(b.ts)
    const byLoud = (a, b) => ((b.conv ?? 0) - (a.conv ?? 0)) || ((b.mag || 0) - (a.mag || 0)) || byAge(a, b)
    const isLoud = (i) => i.conv != null && i.conv >= LOUD
    const newestMs = view.reduce((m, i) => Math.max(m, new Date(i.ts || 0).getTime()), 0)
    const ageFromNewest = (i) => (newestMs - new Date(i.ts || 0).getTime()) / 3.6e6
    const lead = view.filter((i) => ageFromNewest(i) < 24)
    const earlier = view.filter((i) => ageFromNewest(i) >= 24)
    const leadSorted = [...lead.filter(isLoud).sort(byLoud), ...lead.filter((i) => !isLoud(i)).sort(byAge)]
    const leadLabel = newestMs && (Date.now() - newestMs) / 3.6e6 < 24 ? 'TODAY' : 'LATEST'
    return [[leadLabel, leadSorted], ['EARLIER THIS WEEK', [...earlier].sort(byAge)]].filter(([, a]) => a.length)
  }, [filter, typeF, signalItems, newsItems])

  // While-You-Slept: the loudest signal in the true overnight window (8-18h
  // old), ranked conviction → premium → recency; falls back to the full set.
  // Mirrors mobile Wire's overnight logic.
  const overnight = useMemo(() => {
    const night = signalItems.filter((s) => ageHours(s.ts) >= 8 && ageHours(s.ts) <= 18)
    const pool = night.length ? night : signalItems
    if (!pool.length) return null
    return [...pool].sort((a, b) =>
      ((b.conv ?? 0) - (a.conv ?? 0)) || ((b.prem ?? 0) - (a.prem ?? 0)) || (ageHours(a.ts) - ageHours(b.ts)))[0]
  }, [signalItems])

  const filters = [['all', 'ALL'], ['signals', 'SIGNALS'], ['news', 'NEWS']]

  return (
    <>
      <div className="v3web-phead">
        <div className="k">The Wire</div>
        <div className="h" style={{ fontStyle: 'italic' }}>Signals</div>
        <div style={{ marginTop: 8, fontFamily: 'var(--studio-font-mono)', fontSize: 10.5, letterSpacing: '0.08em', color: 'hsl(var(--studio-text-subtle))' }}>
          {signalItems.length} SIGNALS · {newsItems.length} NEWS
        </div>
      </div>

      {!overnight && sigLoading && (
        <div style={{ borderRadius: 'var(--studio-radius)', border: '1px solid hsl(var(--studio-border))', background: 'hsl(var(--studio-surface))', padding: '16px 18px', marginBottom: 22, display: 'flex', flexDirection: 'column', gap: 10 }}>
          <SkelText w={120} h={9} />
          <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}><SkelCircle size={22} /><SkelText w={52} h={12} /></div>
          <SkelText w="70%" h={18} />
        </div>
      )}
      {overnight && (
        <div className="v3web-enter" style={{ borderRadius: 'var(--studio-radius)', border: '1px solid hsl(247 40% 55% / 0.4)', background: 'hsl(247 45% 12% / 0.55)', padding: '16px 18px', marginBottom: 22, cursor: 'pointer' }} onClick={() => openNews(signalToSheet(overnight))}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 9 }}>
            <span style={{ fontFamily: 'var(--studio-font-mono)', fontSize: 9, letterSpacing: '0.16em', color: 'hsl(247 60% 78%)' }}>WHILE YOU SLEPT</span>
            <SignalBadge type={overnight.type} />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 6 }}>
            <StockLogo ticker={overnight.t} size={22} />
            <span style={{ fontFamily: 'var(--studio-font-mono)', fontSize: 14, color: 'hsl(var(--studio-text))' }}>{overnight.t}</span>
          </div>
          <div style={{ fontFamily: 'var(--studio-font-display)', fontSize: 21, lineHeight: 1.3, color: 'hsl(247 46% 92%)' }}>{overnight.headline}</div>
        </div>
      )}

      {alerts && alerts.length > 0 && (
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 24 }}>
          {alerts.slice(0, 6).map((a) => (
            <div key={a.ticker} onClick={() => openTicker(a.ticker)} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '7px 12px', borderRadius: 999, border: '1px solid hsl(var(--studio-accent) / 0.35)', cursor: 'pointer' }}>
              <StockLogo ticker={a.ticker} size={16} />
              <span style={{ fontFamily: 'var(--studio-font-mono)', fontSize: 11, color: 'hsl(var(--studio-text))' }}>{a.ticker}</span>
              <span style={{ fontFamily: 'var(--studio-font-mono)', fontSize: 9.5, color: 'hsl(var(--studio-accent))' }}>{a.signal_count ?? (a.types || []).length} SIGNALS</span>
            </div>
          ))}
        </div>
      )}

      <div style={{ position: 'sticky', top: 0, background: 'hsl(var(--studio-background))', zIndex: 5, paddingBottom: 4 }}>
        <div style={{ display: 'flex', gap: 6, marginBottom: 4 }}>
          {filters.map(([k, l]) => (
            <button key={k} onClick={() => setFilter(k)} style={{ all: 'unset', cursor: 'pointer', fontFamily: 'var(--studio-font-mono)', fontSize: 10, letterSpacing: '0.1em', padding: '5px 6px', borderBottom: filter === k ? '1.5px solid hsl(var(--studio-accent))' : '1.5px solid transparent', color: filter === k ? 'hsl(var(--studio-accent))' : 'hsl(var(--studio-text-muted))' }}>{l}</button>
          ))}
        </div>
        {filter === 'signals' && sigTypes.length > 0 && (
          <div className="atlas-scroll" style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 6 }}>
            {['all', ...sigTypes].map((t) => {
              const on = typeF === t
              const label = t === 'all' ? 'ALL TYPES' : (SIGNAL_MAP[t]?.label || t).toUpperCase()
              return (
                <button key={t} onClick={() => setTypeF(t)} style={{ flex: '0 0 auto', fontFamily: 'var(--studio-font-mono)', fontSize: 9, letterSpacing: '0.08em', padding: '4px 10px', borderRadius: 999, cursor: 'pointer', whiteSpace: 'nowrap', background: on ? 'hsl(var(--studio-surface-2))' : 'transparent', color: on ? 'hsl(var(--studio-text))' : 'hsl(var(--studio-text-subtle))', border: `1px solid ${on ? 'hsl(var(--studio-border))' : 'hsl(var(--studio-border) / 0.5)'}` }}>{label}</button>
              )
            })}
          </div>
        )}
      </div>

      <div>
        {groups.length === 0 && (sigLoading || newsLoading) && (
          <Crossfade loading skeleton={<div>{[0, 1, 2, 3, 4, 5].map((i) => <SkelDisp key={i} i={i} />)}</div>} />
        )}
        {groups.map(([label, arr]) => (
          <div key={label}>
            <div style={{ fontFamily: 'var(--studio-font-mono)', fontSize: 9, letterSpacing: '0.18em', color: 'hsl(var(--studio-text-subtle))', padding: '16px 0 6px' }}>{label} · {arr.length}</div>
            {arr.map((it, ri) => {
              const up = it.sentiment === 'bullish'; const down = it.sentiment === 'bearish'
              const col = up ? '--studio-success' : down ? '--studio-danger' : '--studio-text-subtle'
              const isStarred = starred.has(it.t)
              return (
                <div key={it.id} className="v3web-disp" onClick={() => openNews(signalToSheet(it))} style={enterStyle(ri)}>
                  <div style={{ display: 'flex', gap: 10 }}>
                    <span className="v3web-pip" style={{ background: `hsl(var(${col}))` }} />
                    <span className="time">{fmtTime(it.ts)}</span>
                  </div>
                  <div className="v3web-disp-main">
                    <div className="v3web-disp-meta" onClick={(e) => { e.stopPropagation(); openTicker(it.t) }} style={{ cursor: 'pointer' }}>
                      <StockLogo ticker={it.t} size={20} />
                      <span className="tk">{it.t}</span>
                      {it.kind === 'signal' ? <SignalBadge type={it.type} /> : <span className="src">{it.why}</span>}
                      {it.conv != null && <span style={{ fontFamily: 'var(--studio-font-mono)', fontSize: 9, color: 'hsl(var(--studio-text-subtle))', letterSpacing: '0.06em' }}>CONV {Math.round(it.conv * 100)}</span>}
                    </div>
                    <h3 className="v3web-disp-head">{it.headline}</h3>
                    {it.summary && <p className="v3web-disp-why">{it.summary}</p>}
                    {it.prem > 0 && maxPrem > 0 && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
                        <span style={{ width: `${Math.max(4, (it.prem / maxPrem) * 140)}px`, height: 3, borderRadius: 2, background: `hsl(var(${col}) / 0.55)`, flexShrink: 0 }} />
                        <span style={{ fontFamily: 'var(--studio-font-mono)', fontSize: 9, letterSpacing: '0.06em', color: 'hsl(var(--studio-text-subtle))' }}>{fmtPrem(it.prem)} PREMIUM</span>
                      </div>
                    )}
                  </div>
                  <div className="v3web-disp-sent">
                    {it.sentiment && (
                      <>
                        <span className="arr" style={{ color: `hsl(var(${col}))` }}>{up ? '↑' : '↓'}</span>
                        <span className="lab" style={{ color: `hsl(var(${col}))` }}>{it.sentiment.toUpperCase()}</span>
                      </>
                    )}
                    <button
                      onClick={(e) => { e.stopPropagation(); if (!isStarred) onStar(it.t) }}
                      aria-label={`Watch ${it.t}`} title={isStarred ? 'On your watchlist' : 'Add to watchlist'}
                      style={{ all: 'unset', cursor: isStarred ? 'default' : 'pointer', marginTop: 4, color: isStarred ? 'hsl(var(--studio-accent))' : 'hsl(var(--studio-text-subtle))' }}
                    >
                      <svg viewBox="0 0 24 24" width="13" height="13" fill={isStarred ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M12 17.75l-6.172 3.245l1.179 -6.873l-5 -4.867l6.9 -1l3.086 -6.253l3.086 6.253l6.9 1l-5 4.867l1.179 6.873l-6.158 -3.245" /></svg>
                    </button>
                  </div>
                </div>
              )
            })}
          </div>
        ))}
        {groups.length === 0 && !sigLoading && !newsLoading && <div className="v3web-empty"><div className="pe-k">Quiet on the wire</div><p>{filter === 'news' ? 'No news yet — check back soon.' : 'Nothing matches this filter.'}</p></div>}
      </div>

      {toast && (
        <div style={{ position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', zIndex: 60, fontFamily: 'var(--studio-font-mono)', fontSize: 11, letterSpacing: '0.04em', color: 'hsl(var(--studio-text))', background: 'hsl(var(--studio-surface-2))', border: '1px solid hsl(var(--studio-border))', borderRadius: 999, padding: '8px 18px', boxShadow: '0 8px 24px -8px hsl(var(--studio-overlay))' }}>
          {toast}
        </div>
      )}
    </>
  )
}
